import {
  doc,
  getDoc,
  onSnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import type { OneClickTemplate } from "../../domain/case/one-click-template";
import { getFirebaseServices } from "./firebase";
import { recordRevision, submitSyncV3Mutations } from "./sync-v3-client";

export type { OneClickTemplate } from "../../domain/case/one-click-template";

type UnknownRecord = Record<string, unknown>;

export interface TemplateFolder {
  id: string;
  name: string;
  sortOrder: number;
  templateIds: string[];
}

export interface StudioSavedReport {
  id: string;
  name: string;
  dimension: string;
  startDate: string;
  endDate: string;
  createdAt: string;
}

export interface CloudConfig {
  primaryHospital: string;
  templates: OneClickTemplate[];
  folders: TemplateFolder[];
  savedReports: StudioSavedReport[];
  updatedAt: string;
  recordRevision: number;
  raw: UnknownRecord;
}

export class ConfigurationConflictError extends Error {
  constructor() {
    super("One Tap settings changed on another device. Studio has reloaded the latest version; review it before trying again.");
    this.name = "ConfigurationConflictError";
  }
}

export class SavedReportsConflictError extends Error {
  constructor() {
    super("Saved reports changed in another Studio session. Reload the latest reports before saving again.");
    this.name = "SavedReportsConflictError";
  }
}

function assertCurrentUser(uid: string): void {
  if (!uid || getFirebaseServices().auth.currentUser?.uid !== uid) {
    throw new Error("The requested workspace does not belong to the signed-in account.");
  }
}

function configReference(uid: string) {
  assertCurrentUser(uid);
  return doc(getFirebaseServices().firestore, "users", uid, "config", "main");
}

function record(value: unknown): UnknownRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as UnknownRecord
    : {};
}

function stringValue(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function timestampValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "toDate" in value) {
    const toDate = (value as { toDate?: unknown }).toDate;
    if (typeof toDate === "function") {
      try {
        const date = (toDate as (this: unknown) => Date).call(value);
        if (date instanceof Date && !Number.isNaN(date.getTime())) return date.toISOString();
      } catch {
        return "";
      }
    }
  }
  return "";
}

export function decodeOneClickTemplates(value: unknown): OneClickTemplate[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry, index) => {
    const raw = record(entry);
    const defaults = record(raw.defaults);
    const nativeBackup = record(raw._iosBackup);
    const id = stringValue(raw.id).trim() || stringValue(raw.templateIdentifier).trim();
    if (!id) return [];
    const hospital = stringValue(defaults.hospital).trim()
      || stringValue(defaults._hospital).trim();
    const anaestheticEquipment = stringValue(
      defaults.anaesthetic_equipment ?? defaults.equipment ?? defaults.anaestheticEquipment,
    );
    const awakeStatus = stringValue(
      defaults.airway_awake_status ?? defaults.awake ?? defaults.airwayAwakeStatus,
    );
    const session = stringValue(defaults.session).trim()
      || stringValue(nativeBackup.session).trim();
    const sortOrder = Number(raw.sortOrder);
    const compatibleDefaults: UnknownRecord = {
      ...defaults,
      hospital,
      _hospital: hospital,
    };
    if (anaestheticEquipment || Object.hasOwn(defaults, "anaesthetic_equipment") || Object.hasOwn(defaults, "equipment")) {
      compatibleDefaults.anaesthetic_equipment = anaestheticEquipment;
      compatibleDefaults.equipment = anaestheticEquipment;
    }
    if (awakeStatus || Object.hasOwn(defaults, "airway_awake_status") || Object.hasOwn(defaults, "awake")) {
      compatibleDefaults.airway_awake_status = awakeStatus;
      compatibleDefaults.awake = awakeStatus;
    }
    if (session) compatibleDefaults.session = session;
    const decoded: OneClickTemplate = {
      ...raw,
      id,
      name: stringValue(raw.name, "One Tap Case"),
      icon: stringValue(raw.icon, "zap"),
      favorite: booleanValue(raw.favorite ?? raw.isFavorite, false),
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : index,
      enabled: booleanValue(raw.enabled, true),
      isDeleted: booleanValue(raw.isDeleted, false),
      deletedAt: timestampValue(raw.deletedAt) || null,
      defaults: compatibleDefaults as OneClickTemplate["defaults"],
    };
    const createdAt = timestampValue(raw.createdAt);
    const updatedAt = timestampValue(raw.updatedAt);
    if (createdAt) decoded.createdAt = createdAt;
    else delete decoded.createdAt;
    if (updatedAt) decoded.updatedAt = updatedAt;
    else delete decoded.updatedAt;
    return [decoded];
  });
}

export function oneClickTemplatesFromConfig(value: unknown): OneClickTemplate[] {
  const raw = record(value);
  const settings = record(raw.settings);
  return decodeOneClickTemplates(
    Array.isArray(settings._onetapTemplates)
      ? settings._onetapTemplates
      : raw.templates,
  );
}

export function encodeOneClickTemplates(
  templates: readonly OneClickTemplate[],
): OneClickTemplate[] {
  return templates.map((template) => {
    const defaults = record(template.defaults);
    const hospital = stringValue(defaults.hospital).trim()
      || stringValue(defaults._hospital).trim();
    const anaestheticEquipment = stringValue(
      defaults.anaesthetic_equipment ?? defaults.equipment ?? defaults.anaestheticEquipment,
    );
    const awakeStatus = stringValue(
      defaults.airway_awake_status ?? defaults.awake ?? defaults.airwayAwakeStatus,
    );
    return {
      ...template,
      defaults: {
        ...defaults,
        hospital,
        _hospital: hospital,
        anaesthetic_equipment: anaestheticEquipment,
        equipment: anaestheticEquipment,
        airway_awake_status: awakeStatus,
        awake: awakeStatus,
      } as OneClickTemplate["defaults"],
    };
  });
}

function foldersFrom(value: unknown): TemplateFolder[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry, index) => {
    const raw = record(entry);
    const id = stringValue(raw.id).trim();
    if (!id) return [];
    return [{
      id,
      name: stringValue(raw.name, "Folder"),
      sortOrder: Number(raw.sortOrder) || index,
      templateIds: Array.isArray(raw.templateIds)
        ? raw.templateIds.filter((item): item is string => typeof item === "string")
        : [],
    }];
  });
}

function savedReportsFrom(value: unknown): StudioSavedReport[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const raw = record(entry);
    const id = stringValue(raw.id).trim();
    const name = stringValue(raw.name).trim();
    if (!id || !name) return [];
    return [{ id, name, dimension: stringValue(raw.dimension, "hospital"), startDate: stringValue(raw.startDate), endDate: stringValue(raw.endDate), createdAt: stringValue(raw.createdAt) }];
  });
}

function cloudConfigFromRaw(value: unknown): CloudConfig {
  const raw = record(value);
  const settings = record(raw.settings);
  return {
    primaryHospital: stringValue(settings.primaryHospital ?? raw.primaryHospital),
    templates: oneClickTemplatesFromConfig(raw),
    folders: foldersFrom(raw.studioTemplateFolders),
    savedReports: savedReportsFrom(raw.studioSavedReports),
    updatedAt: timestampValue(raw.updatedAt),
    recordRevision: recordRevision(raw),
    raw,
  };
}

export async function readCloudConfig(uid: string): Promise<CloudConfig> {
  const snapshot = await getDoc(configReference(uid));
  return cloudConfigFromRaw(snapshot.exists() ? snapshot.data() : {});
}

export function observeCloudConfig(
  uid: string,
  observer: (config: CloudConfig) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(configReference(uid), (snapshot) => {
    observer(cloudConfigFromRaw(snapshot.exists() ? snapshot.data() : {}));
  }, (reason) => onError?.(reason instanceof Error ? reason : new Error(String(reason))));
}

export async function writeSavedReports(
  uid: string,
  reports: StudioSavedReport[],
  expectedUpdatedAt: string,
): Promise<string> {
  const snapshot = await getDoc(configReference(uid));
  const currentData = snapshot.exists() ? snapshot.data() : {};
  const currentUpdatedAt = timestampValue(currentData?.updatedAt);
  if (currentUpdatedAt !== expectedUpdatedAt) throw new SavedReportsConflictError();
  const [result] = await submitSyncV3Mutations(uid, [{
    entityType: "config",
    entityId: "main",
    operation: snapshot.exists() ? "upsert" : "create",
    baseRevision: snapshot.exists() ? recordRevision(currentData) : 0,
    payload: { studioSavedReports: reports },
  }]);
  if (result?.status === "applied" && result.canonicalPayload) {
    return timestampValue(result.canonicalPayload.updatedAt);
  }
  if (result?.canonicalPayload) throw new SavedReportsConflictError();
  throw new Error(result?.message ?? "Saved reports could not be updated.");
}

export async function writeOneClickConfiguration(
  uid: string,
  templates: OneClickTemplate[],
  folders: TemplateFolder[],
  expectedUpdatedAt: string,
): Promise<string> {
  const compatibleTemplates = encodeOneClickTemplates(templates);
  const snapshot = await getDoc(configReference(uid));
  const currentData = snapshot.exists() ? snapshot.data() : {};
  const currentUpdatedAt = timestampValue(currentData?.updatedAt);
  if (currentUpdatedAt !== expectedUpdatedAt) throw new ConfigurationConflictError();
  const payload: UnknownRecord = {
    settings: { _onetapTemplates: compatibleTemplates },
    studioTemplateFolders: folders,
  };
  // Once the canonical settings array exists, leave a distinct legacy
  // top-level array untouched. Native clients preserve it for lossless
  // migration instead of replacing potentially richer old records.
  if (!Array.isArray(currentData?.templates)) payload.templates = compatibleTemplates;
  const [result] = await submitSyncV3Mutations(uid, [{
    entityType: "config",
    entityId: "main",
    operation: snapshot.exists() ? "upsert" : "create",
    baseRevision: snapshot.exists() ? recordRevision(currentData) : 0,
    payload,
  }]);
  if (result?.status === "applied" && result.canonicalPayload) {
    return timestampValue(result.canonicalPayload.updatedAt);
  }
  if (result?.canonicalPayload) throw new ConfigurationConflictError();
  throw new Error(result?.message ?? "One Tap settings could not be updated.");
}
